// Summary-view fetch-bucket routing + the simple/adjusted mode resolver for the pacing table.
// Deliberate dependency-light leaf module — free of config/component-tree imports so it stays cheap
// to import and test in isolation.
import { PerformanceData } from '@blisspointmedia/bpm-types/dist/MetricsTable';
import { normalizeSourceKey } from '@blisspointmedia/bpm-types/dist/Pacing';
import { buildKpiToKey, num } from './pacingPresetColumnPicker';

/** The column fields routing reads — a structural subset of Column so the over-time pivot's lite
 *  column descriptors route through the same implementation. */
interface RoutableColumn {
  dataVarName?: string;
  kpi?: string;
  source?: string;
}

// The table lambda (pacingV2/pacingTable) emits these buckets per row:
//   "delivery"                          — spend / impressions / clicks (source-independent)
//   "<kpiKey>::<srcKey>"                — revenue / kpi_volume, one bucket per (KPI, sources) scope
//   "goal_spend"                        — the Budget goal columns (KPI- and source-agnostic)
//   "goal_revenue::<kpiKey>::<srcKey>" / "goal_volume::<kpiKey>::<srcKey>" — per-variant goal
//     columns, one per (KPI, sources) scope
// Each summary additive goal bucket carries simple_/adjusted_ pairs for goal / projected / run_rate /
// run_rate_future / goal_rows (resolvePacingMode picks the active mode into the bare key), plus a flat
// goal_col_rows = 1 (always, one per row) so the run-rate/Ideal totals row can tell a zero-goal totals
// (render "--") from a single no-goal cell (render 0). On the
// SUMMARY the mode-resolved goal_rows is 1 when that mode booked a goal, else 0. The OVER-TIME
// additive cells instead ship a flat, mode-less goal_rows = 1 per in-window date (and pacing / run
// rate / remainder / run_rate_future for the totals re-derivation) — resolvePacingMode leaves the flat
// goal_rows untouched (no simple_/adjusted_ prefix), and the over-time totals fix relies on it counting
// EVERY in-window date, so keep it emitted per date, not only on goal-bearing ones.
// A column routes to its own KPI's / source's buckets via col.kpi / col.source, falling back to
// the page KPI / page source picker ("Global").
const KPI_BUCKET = '__kpi__';
const BUCKET_BY_DATA_VAR_NAME: Record<string, string> = {
  spend: 'delivery',
  impressions: 'delivery',
  clicks: 'delivery',
  revenue: KPI_BUCKET,
  kpi_volume: KPI_BUCKET,
  spend_toward_goal: 'goal_spend',
  spend_projected: 'goal_spend',
  spend_goal: 'goal_spend',
  spend_pct_to_goal: 'goal_spend',
  spend_pacing: 'goal_spend',
  spend_run_rate: 'goal_spend',
  spend_remainder: 'goal_spend',
  spend_yesterday: 'goal_spend',
  revenue_toward_goal: 'goal_revenue',
  revenue_projected: 'goal_revenue',
  revenue_goal: 'goal_revenue',
  revenue_pct_to_goal: 'goal_revenue',
  revenue_pacing: 'goal_revenue',
  revenue_run_rate: 'goal_revenue',
  revenue_remainder: 'goal_revenue',
  revenue_yesterday: 'goal_revenue',
  volume_toward_goal: 'goal_volume',
  volume_projected: 'goal_volume',
  volume_goal: 'goal_volume',
  volume_pct_to_goal: 'goal_volume',
  volume_pacing: 'goal_volume',
  volume_run_rate: 'goal_volume',
  volume_remainder: 'goal_volume',
  volume_yesterday: 'goal_volume',
  // Ratio metrics: all three variants (Actual / Goal / % to Goal) read one bucket carrying the
  // component sums + flat goal. KPI-scoped (ROAS/CPX) get a "::<kpiKey>::<srcKey>" suffix below;
  // delivery-only (CPC/CPM/CTR) stay bare, like goal_spend.
  roas: 'goal_roas',
  roas_toward_goal: 'goal_roas',
  roas_goal: 'goal_roas',
  roas_pct_to_goal: 'goal_roas',
  cpx: 'goal_cpx',
  cpx_toward_goal: 'goal_cpx',
  cpx_goal: 'goal_cpx',
  cpx_pct_to_goal: 'goal_cpx',
  cpc: 'goal_cpc',
  cpc_toward_goal: 'goal_cpc',
  cpc_goal: 'goal_cpc',
  cpc_pct_to_goal: 'goal_cpc',
  cpm: 'goal_cpm',
  cpm_toward_goal: 'goal_cpm',
  cpm_goal: 'goal_cpm',
  cpm_pct_to_goal: 'goal_cpm',
  ctr: 'goal_ctr',
  ctr_toward_goal: 'goal_ctr',
  ctr_goal: 'goal_ctr',
  ctr_pct_to_goal: 'goal_ctr'
};

/** Goal buckets carrying a per-(KPI, sources) suffix. Delivery-only goal buckets (goal_spend,
 *  goal_cpc/cpm/ctr) are bare. */
const KPI_SCOPED_GOAL_BUCKETS = new Set(['goal_revenue', 'goal_volume', 'goal_roas', 'goal_cpx']);

export const getPacingDataFetchKey =
  (activeKpiKey: string, activeSourceKey: string) =>
  (col: RoutableColumn | undefined): string => {
    if (!col) {
      return 'delivery';
    }
    const bucket = BUCKET_BY_DATA_VAR_NAME[col.dataVarName ?? ''] ?? 'delivery';
    // A column with its own KPI/source reads that scope's buckets; otherwise it follows the page
    // KPI / page source picker.
    const colKpiKey = col.kpi ? buildKpiToKey(col.kpi) : activeKpiKey;
    const colSourceKey = col.source ? normalizeSourceKey([col.source]) : activeSourceKey;
    if (bucket === KPI_BUCKET) {
      return `${colKpiKey}::${colSourceKey}`;
    }
    if (KPI_SCOPED_GOAL_BUCKETS.has(bucket)) {
      return `${bucket}::${colKpiKey}::${colSourceKey}`;
    }
    return bucket;
  };

// PerformanceData.fetches is typed Record<string, string | number> in bpm-types but is nested at
// runtime (Record<bucket, Record<colName, value>>). Cast through unknown to read cross-bucket values.
const bucketsOf = (row: unknown): Record<string, Record<string, string | number>> | undefined =>
  (row as PerformanceData | undefined)?.fetches as
    Record<string, Record<string, string | number>> | undefined;

/**
 * Resolve the simple/adjusted toggle into the fixed keys the configs read. The lambda ships both
 * simple_* and adjusted_* for goal / projected / run_rate (and, in the over-time payload, pacing /
 * remainder); this picks the active mode (mode-independent keys toward/yesterday pass through) so the
 * column config and totals stay mode-agnostic. Only keys the payload actually shipped are resolved, so
 * it's safe for both views (summary derives pacing/remainder in-config and never ships them).
 */
export const resolvePacingMode = (
  data: PerformanceData[],
  isAdjusted: boolean
): PerformanceData[] => {
  const pick = isAdjusted ? 'adjusted_' : 'simple_';
  return data.map(row => {
    const buckets = bucketsOf(row);
    if (!buckets) {
      return row;
    }
    const resolvedFetches: Record<string, Record<string, string | number>> = { ...buckets };
    // Goal buckets: "goal_spend" plus one "goal_revenue::<kpiKey>::<srcKey>" /
    // "goal_volume::<kpiKey>::<srcKey>" per (KPI, sources) scope.
    for (const bucketKey of Object.keys(buckets).filter(k => k.startsWith('goal_'))) {
      const b = buckets[bucketKey];
      if (!b) {
        continue;
      }
      // Only resolve a mode key when the payload actually shipped it — otherwise the over-time
      // "absent = render --" signal (e.g. projected on past rows, pacing on future rows) would be
      // overwritten with a 0.
      const resolved: Record<string, string | number> = { ...b };
      for (const key of [
        'goal',
        'projected',
        'run_rate',
        'run_rate_future',
        'pacing',
        'remainder',
        'goal_rows'
      ]) {
        if (`${pick}${key}` in b) {
          resolved[key] = num(b[`${pick}${key}`]);
        }
      }
      resolvedFetches[bucketKey] = resolved;
    }
    return { ...row, fetches: resolvedFetches } as unknown as PerformanceData;
  });
};
